import { useState, type FormEvent, type ReactNode } from 'react'
import { ArrowSquareOut, Check, Headset, Key, SquaresFour } from './ui/icons'
import { SandboxAccessCard, useSharedSandboxAccess } from './SandboxAccessCard'
import { SectionHeader } from './ui/primitives'
import { applyHouseNoun, houseNoun } from '../lib/house-noun'
import type { CustomerSegment, SharedSandboxAccess, WorkspaceAsset } from '../types'

/**
 * The Product and Case Studies tabs. Customer workspaces render them with the
 * workspace's assets; the partner area renders the same components with the
 * standard pre-deal library, so both stay in sync when a tab changes.
 */

type Track = (eventType: string, targetId?: string) => void

/** Where the Product tab gets the shared sandbox accounts, and what it shows when they do not load. */
export interface ProductSandboxSource {
  /** Changes when the accounts must be loaded again, for example the workspace or partner slug. */
  key: string
  load: () => Promise<SharedSandboxAccess>
  fallback: ReactNode
}

export function ProductTab({ assets: allAssets, segment, sandbox, showHeader = true, onTrack }: {
  assets: WorkspaceAsset[]
  segment: CustomerSegment | null
  /** Null hides the sandbox card. */
  sandbox: ProductSandboxSource | null
  /** False hides the tab heading, for a page that has its own. */
  showHeader?: boolean
  onTrack?: Track
}) {
  const assets = allAssets.filter((asset) => asset.sectionKey === 'product')
  const featureAssets = assets.filter((asset) => !['iframe', 'video', 'demo_credentials'].includes(asset.type))
  const mediaAssets = assets.filter((asset) => asset.type === 'iframe' || asset.type === 'video')
  const credentialsAssets = assets.filter((asset) => asset.type === 'demo_credentials')
  const sharedSandboxAccess = useSharedSandboxAccess(sandbox?.key ?? null, sandbox?.load)

  return (
    <section className="standard-tab">
      {showHeader && <SectionHeader title="Product" description={`One connected system that gives every ${houseNoun(segment).singular} useful guidance and gives your team a clear view of career outcomes.`} />}
      <AiAgentExplainer segment={segment} />
      {sandbox && (sharedSandboxAccess === undefined
        ? <div className="sandbox-access-loading" aria-label="Loading sandbox access" />
        : sharedSandboxAccess
        ? <SandboxAccessCard access={sharedSandboxAccess} segment={segment} />
        : sandbox.fallback)}

      {mediaAssets.map((asset, index) => (
        <section className={index % 2 === 1 ? 'product-tour product-tour-reverse' : 'product-tour'} key={asset.id}>
          <div><span>{applyHouseNoun(String(asset.metadata?.category || 'Product walkthrough'), segment)}</span><h3>{applyHouseNoun(asset.title, segment)}</h3><p>{applyHouseNoun(asset.description || '', segment)}</p></div>
          {asset.url && (
            <div className="responsive-embed">
              <iframe src={asset.url} title={asset.title} loading="lazy" allow="autoplay; fullscreen; picture-in-picture" allowFullScreen sandbox="allow-scripts allow-same-origin allow-popups allow-popups-to-escape-sandbox allow-forms" referrerPolicy="strict-origin-when-cross-origin" onLoad={() => onTrack?.('asset_view', asset.id)} />
            </div>
          )}
        </section>
      ))}

      <div className="subsection-heading"><h3>What teams use every day</h3><p>The core capabilities that move {houseNoun(segment).plural} from career preparation to verified employment.</p></div>
      <div className="feature-list">
        {featureAssets.map((asset) => {
          const image = typeof asset.metadata?.image === 'string' ? asset.metadata.image : null
          const features = Array.isArray(asset.metadata?.features) ? asset.metadata.features.filter((item): item is string => typeof item === 'string') : []
          const title = applyHouseNoun(asset.title, segment)
          const description = applyHouseNoun(asset.description || '', segment)
          const category = applyHouseNoun(String(asset.metadata?.category || 'Prentus platform'), segment)
          const content = <>{image ? <img className="feature-image" src={image} alt={`${title} in Prentus`} /> : <span className="feature-icon green"><SquaresFour size={20} /></span>}<div><span>{category}</span><h3>{title}</h3><p>{description}</p>{features.length > 0 && <div className="feature-tags">{features.map((feature) => <span key={feature}>{feature}</span>)}</div>}</div>{asset.url && <span className="row-action">Learn more<ArrowSquareOut size={15} /></span>}</>
          return asset.url
            ? <a className="feature-row" key={asset.id} href={asset.url} target="_blank" rel="noreferrer" onClick={() => onTrack?.('asset_open', asset.id)}>{content}</a>
            : <article className="feature-row" key={asset.id}>{content}</article>
        })}
        {credentialsAssets.map((asset) => <DemoCredentialsCard key={asset.id} asset={asset} onTrack={onTrack} />)}
      </div>
    </section>
  )
}

export function SandboxRequestCard({ onRequest }: { onRequest?: (context: string) => Promise<void> }) {
  const [context, setContext] = useState('')
  const [state, setState] = useState<'idle' | 'open' | 'submitting' | 'submitted' | 'error'>('idle')
  const [error, setError] = useState('')

  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault()
    if (state === 'submitting' || !onRequest) return
    setState('submitting')
    setError('')
    try {
      await onRequest(context.trim())
      setState('submitted')
    } catch (cause) {
      setState('error')
      setError(cause instanceof Error ? cause.message : 'Your request could not be saved.')
    }
  }

  return (
    <section className="sandbox-request" aria-labelledby="sandbox-request-title">
      <div className="sandbox-request-copy">
        <span className="sandbox-request-icon"><Headset size={21} /></span>
        <div><h3 id="sandbox-request-title">Explore Prentus with your team</h3><p>Request a private sandbox configured around the workflows you want to evaluate.</p></div>
        {state !== 'submitted' && <button type="button" className="button button-secondary" disabled={!onRequest || state === 'submitting'} onClick={() => setState(state === 'open' || state === 'error' ? 'idle' : 'open')}>{!onRequest ? 'Available in customer view' : state === 'open' || state === 'error' ? 'Close' : 'Request sandbox'}</button>}
      </div>
      {state === 'submitted' ? <div className="sandbox-request-success"><strong>Request received.</strong><span>We’ll review it and follow up with next steps.</span></div> : (
        (state === 'open' || state === 'submitting' || state === 'error') && <form onSubmit={(event) => void submit(event)}>
          <label><span>What should we configure? <small>Optional</small></span><textarea autoFocus value={context} rows={2} placeholder="Advisor workflows, integrations, student activation, or something else" onChange={(event) => setContext(event.target.value)} /></label>
          <div className="sandbox-request-actions"><small>Submitting creates a request for the Prentus team. Nothing is provisioned automatically.</small><button type="submit" className="button button-primary" disabled={state === 'submitting' || !onRequest}>{state === 'submitting' ? 'Sending request…' : 'Send request'}</button></div>
          {error && <p className="form-error">{error}</p>}
        </form>
      )}
    </section>
  )
}

function AiAgentExplainer({ segment }: { segment: CustomerSegment | null }) {
  const noun = houseNoun(segment)
  return (
    <section className="agent-explainer" aria-labelledby="agent-explainer-title">
      <div className="agent-copy">
        <span>How the AI agent works</span>
        <h3 id="agent-explainer-title">Guidance that already knows the {noun.singular}</h3>
        <p>Prentus does more than answer a prompt. The agent uses the {noun.singular}’s context, notices what is missing, and helps them take the next useful action.</p>
        <ol>
          <li><b>1</b><span><strong>Understands context</strong><small>Program, goals, materials, applications, and advisor guidance</small></span></li>
          <li><b>2</b><span><strong>Guides action</strong><small>Practice, revise, apply, follow up, or ask for human help</small></span></li>
          <li><b>3</b><span><strong>Keeps the team informed</strong><small>Engagement signals and outcomes flow back to the advisor view</small></span></li>
        </ol>
      </div>
      <figure className="agent-product-visual">
        <img src="/images/product/ai-agent.webp" alt={`Prentus AI Career Advisor guiding a ${noun.singular}`} />
        <figcaption>Personal guidance built from each {noun.singular}’s goals, materials, and progress.</figcaption>
      </figure>
    </section>
  )
}

function DemoCredentialsCard({ asset, onTrack }: { asset: WorkspaceAsset; onTrack?: Track }) {
  const [credentials, setCredentials] = useState<{ loginUrl: string; username: string; password: string } | null>(null)
  const [error, setError] = useState('')
  const [loading, setLoading] = useState(false)

  async function reveal() {
    if (!asset.url || loading) return
    setLoading(true)
    setError('')
    try {
      const response = await fetch(asset.url, { credentials: 'same-origin', cache: 'no-store' })
      const body = await response.json().catch(() => ({}))
      if (!response.ok) throw new Error(typeof body.error === 'string' ? body.error : 'Credentials could not be opened.')
      setCredentials(body)
      onTrack?.('asset_open', asset.id)
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'Credentials could not be opened.')
    } finally {
      setLoading(false)
    }
  }

  return (
    <article className="demo-credentials-card">
      <span className="feature-icon secure"><Key size={19} weight="duotone" /></span>
      <div><span>Secure demo access</span><h3>{asset.title}</h3><p>{asset.description}</p>{credentials && <dl><div><dt>Username</dt><dd>{credentials.username}</dd></div><div><dt>Password</dt><dd>{credentials.password}</dd></div></dl>}{error && <small className="form-error">{error}</small>}</div>
      {credentials ? <a href={credentials.loginUrl} target="_blank" rel="noreferrer">Open demo<ArrowSquareOut size={15} /></a> : <button type="button" onClick={() => void reveal()} disabled={loading}>{loading ? 'Opening...' : 'View credentials'}</button>}
    </article>
  )
}

export function CaseStudiesTab({ assets: allAssets, segment, showHeader = true, onTrack }: {
  assets: WorkspaceAsset[]
  segment: CustomerSegment | null
  /** False hides the tab heading, for a page that has its own. */
  showHeader?: boolean
  onTrack?: Track
}) {
  const assets = allAssets.filter((asset) => asset.sectionKey === 'case-studies')
  const caseStudies = assets.filter((asset) => asset.type !== 'iframe')
  const studentLove = assets.find((asset) => asset.type === 'iframe')
  return (
    <section className="standard-tab">
      {showHeader && <SectionHeader title="Case Studies" description={`Results from career teams that needed to serve more ${houseNoun(segment).plural}, cut manual work, and prove outcomes.`} />}
      <div className="case-study-grid">
        {caseStudies.map((asset) => {
          const outcomes = Array.isArray(asset.metadata?.outcomes) ? asset.metadata.outcomes.filter((item): item is string => typeof item === 'string') : []
          const logo = typeof asset.metadata?.logo === 'string' ? asset.metadata.logo : null
          const customer = String(asset.metadata?.customer || 'Prentus customer')
          const sourceUrl = typeof asset.metadata?.sourceUrl === 'string' ? asset.metadata.sourceUrl : null
          return (
            <article key={asset.id}>
              <div className="case-brand">{logo ? <img src={logo} alt={`${customer} logo`} /> : <span>{customer}</span>}</div>
              {Boolean(asset.metadata?.stat) && <strong className="case-stat">{String(asset.metadata?.stat)}</strong>}
              <h3>{asset.title}</h3>
              <p>{asset.description}</p>
              {outcomes.length > 0 && <ul>{outcomes.map((outcome) => <li key={outcome}><Check size={14} weight="bold" />{outcome}</li>)}</ul>}
              {sourceUrl && <a className="case-source-link" href={sourceUrl} target="_blank" rel="noreferrer" onClick={() => onTrack?.('asset_open', `${asset.id}-source`)}>Source<ArrowSquareOut size={13} /></a>}
              {asset.url && <a className="row-action" href={asset.url} target="_blank" rel="noreferrer" onClick={() => onTrack?.('asset_open', asset.id)}>Read case study<ArrowSquareOut size={15} /></a>}
            </article>
          )
        })}
      </div>
      {studentLove?.url && (
        <section className="student-love">
          <div><span>{houseNoun(segment).singular.charAt(0).toUpperCase() + houseNoun(segment).singular.slice(1)} proof</span><h3>{applyHouseNoun(studentLove.title, segment)}</h3><p>{applyHouseNoun(studentLove.description || '', segment)}</p><a href="https://love.prentus.com/all" target="_blank" rel="noreferrer" onClick={() => onTrack?.('asset_open', studentLove.id)}>Open the full Wall of Love<ArrowSquareOut size={15} /></a></div>
          <div className="testimonial-embed"><iframe src={studentLove.url} title={studentLove.title} loading="lazy" allow="fullscreen" sandbox="allow-scripts allow-same-origin allow-popups allow-popups-to-escape-sandbox" referrerPolicy="strict-origin-when-cross-origin" onLoad={() => onTrack?.('asset_view', studentLove.id)} /></div>
        </section>
      )}
    </section>
  )
}
